import React, { useMemo } from 'react';
import { Card } from 'antd';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  formatVnCalendarDate,
  formatVnChartDay,
  vnDayjs,
} from '../../utils/vnDateTime';
import { formatVnd } from '../../utils/formatVnd';
import type { RevenueTrendData } from '../../services/dashboardService';
import { ChartSkeleton } from '../dashboard/ChartSkeleton';
import { DashboardEmptyState } from '../dashboard/DashboardEmptyState';

function fillMissingDates(
  items: RevenueTrendData[],
  startDate: string,
  endDate: string
): RevenueTrendData[] {
  const byDate = new Map<string, RevenueTrendData>();
  items.forEach(item => byDate.set(item.date, item));
  const result: RevenueTrendData[] = [];
  const start = vnDayjs(startDate).startOf('day');
  const end = vnDayjs(endDate).endOf('day');
  for (
    let d = start;
    d.isSame(end, 'day') || d.isBefore(end, 'day');
    d = d.add(1, 'day')
  ) {
    const key = d.format('YYYY-MM-DD');
    result.push(byDate.get(key) ?? { date: key, revenue: 0, orders: 0 });
  }
  return result;
}

function chartTitle(base: string, rangeLabel?: string) {
  return rangeLabel ? `${base} · ${rangeLabel}` : base;
}

interface MerchantRevenueChartProps {
  data: RevenueTrendData[];
  loading?: boolean;
  rangeLabel?: string;
}

export const MerchantRevenueChart: React.FC<MerchantRevenueChartProps> = ({
  data,
  loading = false,
  rangeLabel,
}) => {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    const minDate = data.reduce((a, b) => (a.date < b.date ? a : b)).date;
    const maxDate = data.reduce((a, b) => (a.date > b.date ? a : b)).date;
    const filled = fillMissingDates(data, minDate, maxDate);
    return filled.map(item => ({
      ...item,
      label: formatVnChartDay(item.date),
    }));
  }, [data]);

  const title = chartTitle('Doanh thu theo ngày', rangeLabel);

  if (loading) {
    return (
      <Card className="merchant-detail-analytics-chart-card" title={title}>
        <ChartSkeleton height={280} />
      </Card>
    );
  }

  if (!chartData.length) {
    return (
      <Card className="merchant-detail-analytics-chart-card" title={title}>
        <div className="merchant-detail-analytics-chart-placeholder">
          <DashboardEmptyState
            message="Chưa có dữ liệu doanh thu"
            suggestion="Merchant chưa có đơn thành công trong khoảng thời gian này."
          />
        </div>
      </Card>
    );
  }

  return (
    <Card className="merchant-detail-analytics-chart-card" title={title}>
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart
          data={chartData}
          margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
        >
          <defs>
            <linearGradient
              id="merchantRevenueGrad"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#e5e7eb"
            vertical={false}
          />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis
            tickFormatter={v =>
              v >= 1000000
                ? `${(v / 1000000).toFixed(1)}tr`
                : v >= 1000
                ? `${(v / 1000).toFixed(0)}k`
                : String(v)
            }
            width={52}
          />
          <Tooltip
            formatter={(value: number) => [formatVnd(value), 'Doanh thu']}
            labelFormatter={label => {
              const item = chartData.find(d => d.label === label);
              return item
                ? formatVnCalendarDate(item.date, 'DD/MM/YYYY')
                : label;
            }}
          />
          <Area
            type="monotone"
            dataKey="revenue"
            stroke="#2563eb"
            strokeWidth={2}
            fill="url(#merchantRevenueGrad)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </Card>
  );
};

interface MerchantOrdersChartProps {
  data: RevenueTrendData[];
  loading?: boolean;
  rangeLabel?: string;
}

export const MerchantOrdersChart: React.FC<MerchantOrdersChartProps> = ({
  data,
  loading = false,
  rangeLabel,
}) => {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    const minDate = data.reduce((a, b) => (a.date < b.date ? a : b)).date;
    const maxDate = data.reduce((a, b) => (a.date > b.date ? a : b)).date;
    const filled = fillMissingDates(data, minDate, maxDate);
    return filled.map(item => ({
      ...item,
      label: formatVnChartDay(item.date),
    }));
  }, [data]);

  const title = chartTitle('Số đơn theo ngày', rangeLabel);

  if (loading) {
    return (
      <Card className="merchant-detail-analytics-chart-card" title={title}>
        <ChartSkeleton height={280} />
      </Card>
    );
  }

  if (!chartData.length) {
    return (
      <Card className="merchant-detail-analytics-chart-card" title={title}>
        <div className="merchant-detail-analytics-chart-placeholder">
          <DashboardEmptyState
            message="Chưa có dữ liệu đơn hàng"
            suggestion="Merchant chưa nhận đơn trong khoảng thời gian này."
          />
        </div>
      </Card>
    );
  }

  return (
    <Card className="merchant-detail-analytics-chart-card" title={title}>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart
          data={chartData}
          margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#e5e7eb"
            vertical={false}
          />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis allowDecimals={false} width={40} />
          <Tooltip
            formatter={(value: number) => [
              value.toLocaleString('vi-VN'),
              'Số đơn',
            ]}
            labelFormatter={label => {
              const item = chartData.find(d => d.label === label);
              return item
                ? formatVnCalendarDate(item.date, 'DD/MM/YYYY')
                : label;
            }}
          />
          <Bar dataKey="orders" fill="#10b981" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
};
